<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
              <div>
                <h4 class="mb-25">Assign Tracking</h4>
                <p class="text-muted mb-0 small">
                  Full audit trail of lead assignments — bulk assign, transfer, and edit form changes.
                </p>
              </div>
              <b-button variant="outline-secondary" size="sm" @click="$router.push('/crm/lead')">
                <feather-icon icon="ArrowLeftIcon" class="mr-50" />
                Back to Leads
              </b-button>
            </div>

            <b-card-code class="mb-2">
              <b-row>
                <b-col md="3">
                  <b-form-group label="Search lead">
                    <b-form-input
                      v-model="filters.search"
                      placeholder="Name, mobile, email"
                      @keyup.enter="applyFilters"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Source">
                    <v-select
                      v-model="filters.source"
                      :options="sourceOptions"
                      label="label"
                      :reduce="(o) => o.value"
                      placeholder="All sources"
                      :clearable="true"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group label="Date range">
                    <flat-pickr
                      v-model="filters.dateRange"
                      class="form-control"
                      :config="{ dateFormat: 'd/m/Y', mode: 'range' }"
                      placeholder="Select date range"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Lead ID">
                    <b-form-input
                      v-model="filters.lead_id"
                      type="number"
                      placeholder="Optional"
                      min="1"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2" class="d-flex align-items-end">
                  <b-button variant="primary" class="mr-50" :disabled="loading" @click="applyFilters">
                    <feather-icon icon="SearchIcon" class="mr-50" />
                    Search
                  </b-button>
                  <b-button variant="outline-secondary" :disabled="loading" @click="resetFilters">
                    Clear
                  </b-button>
                </b-col>
              </b-row>
            </b-card-code>

            <div v-if="loading" class="text-center py-3">
              <b-spinner label="Loading" />
            </div>

            <b-table
              v-else
              responsive
              striped
              hover
              :items="rows"
              :fields="fields"
              show-empty
              empty-text="No assignment logs found for the selected filters."
              class="assign-tracking-table"
            >
              <template #cell(source_label)="data">
                <b-badge :variant="sourceVariant(data.item.source)">
                  {{ data.value }}
                </b-badge>
              </template>
              <template #cell(lead)="data">
                <div v-if="data.item.lead">
                  <router-link
                    :to="`/crm/lead/profile/${data.item.lead.id}`"
                    class="font-weight-bold"
                  >
                    {{ data.item.lead.fullname || data.item.lead.name }}
                  </router-link>
                  <div class="small text-muted">{{ data.item.lead.mobile }}</div>
                </div>
                <span v-else class="text-muted">#{{ data.item.lead_id }}</span>
              </template>
              <template #cell(from_to)="data">
                <span class="small">
                  {{ userLabel(data.item.transfer_from_user) }}
                  <feather-icon icon="ArrowRightIcon" size="12" class="mx-25" />
                  {{ userLabel(data.item.assignee) }}
                </span>
              </template>
              <template #cell(performed_by)="data">
                {{ userLabel(data.item.transfer_by_user) }}
              </template>
              <template #cell(created_at)="data">
                {{ formatDate(data.value) }}
              </template>
              <template #cell(batch_id)="data">
                <span v-if="data.value" class="small text-monospace" :title="data.value">
                  {{ shortBatch(data.value) }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>
            </b-table>

            <div
              v-if="pagination.total > 0"
              class="d-flex flex-wrap justify-content-between align-items-center mt-1"
            >
              <span class="text-muted small">
                Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}
              </span>
              <b-pagination
                v-model="pagination.page"
                :total-rows="pagination.total"
                :per-page="pagination.per_page"
                align="right"
                @change="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/components/axios";
import moment from "moment";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import BCardCode from "@core/components/b-card-code";
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from "bootstrap-vue";

export default {
  name: "AssignTracking",
  components: {
    flatPickr,
    vSelect,
    BCardCode,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
  },
  data() {
    return {
      accessToken: localStorage.getItem("accessToken"),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      loading: false,
      rows: [],
      sourceOptions: [],
      filters: {
        search: "",
        source: null,
        dateRange: "",
        lead_id: "",
      },
      pagination: {
        page: 1,
        per_page: 25,
        total: 0,
        from: 0,
        to: 0,
      },
      fields: [
        { key: "created_at", label: "Date & time", sortable: false },
        { key: "source_label", label: "Source", sortable: false },
        { key: "lead", label: "Lead", sortable: false },
        { key: "from_to", label: "From → To", sortable: false },
        { key: "performed_by", label: "Performed by", sortable: false },
        { key: "remarks", label: "Remarks", sortable: false },
        { key: "batch_id", label: "Batch", sortable: false },
      ],
    };
  },
  mounted() {
    this.loadSources();
    this.fetchLogs();
    if (this.$route.query.lead_id) {
      this.filters.lead_id = String(this.$route.query.lead_id);
    }
  },
  methods: {
    async loadSources() {
      try {
        const res = await axios({
          method: "GET",
          url: `${this.baseApi}/lead-assign-logs/sources`,
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        this.sourceOptions = res.data?.data || [];
      } catch (e) {
        this.sourceOptions = [
          { value: "bulk_assign", label: "Bulk Assign" },
          { value: "transfer", label: "Transfer" },
          { value: "form_edit", label: "Lead Edit" },
          { value: "initial_assign", label: "Initial Assignment" },
        ];
      }
    },
    buildParams() {
      const params = {
        page: this.pagination.page,
        per_page: this.pagination.per_page,
      };
      if (this.filters.search?.trim()) {
        params.search = this.filters.search.trim();
      }
      if (this.filters.source) {
        params.source = this.filters.source;
      }
      if (this.filters.lead_id) {
        params.lead_id = this.filters.lead_id;
      }
      if (this.filters.dateRange) {
        const parts = this.filters.dateRange.split(" to ");
        if (parts[0]) {
          params.date_from = parts[0].trim();
        }
        if (parts[1]) {
          params.date_to = parts[1].trim();
        } else if (parts[0]) {
          params.date_to = parts[0].trim();
        }
      }
      return params;
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const res = await axios({
          method: "GET",
          url: `${this.baseApi}/lead-assign-logs`,
          headers: { Authorization: `Bearer ${this.accessToken}` },
          params: this.buildParams(),
        });
        const payload = res.data?.data;
        this.rows = payload?.data || [];
        this.pagination.total = payload?.total || 0;
        this.pagination.from = payload?.from || 0;
        this.pagination.to = payload?.to || 0;
        this.pagination.page = payload?.current_page || 1;
        this.pagination.per_page = payload?.per_page || 25;
      } catch (e) {
        this.rows = [];
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.pagination.page = 1;
      this.fetchLogs();
    },
    resetFilters() {
      this.filters = { search: "", source: null, dateRange: "", lead_id: "" };
      this.pagination.page = 1;
      this.fetchLogs();
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.fetchLogs();
    },
    userLabel(user) {
      if (!user || !user.id) {
        return "Unassigned";
      }
      return user.fullname || user.name || user.username || `#${user.id}`;
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD MMM YYYY, hh:mm A");
    },
    sourceVariant(source) {
      const map = {
        bulk_assign: "primary",
        transfer: "warning",
        form_edit: "info",
        initial_assign: "success",
      };
      return map[source] || "secondary";
    },
    shortBatch(id) {
      if (!id || id.length < 12) {
        return id || "";
      }
      return `${id.slice(0, 8)}…`;
    },
  },
};
</script>

<style scoped>
.assign-tracking-table {
  font-size: 0.9rem;
}
</style>
